import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { LAMPORTS_PER_SOL, Transaction, PublicKey } from '@solana/web3.js';
import { fetchAllPopperNFTs, fetchNFTMetadata, buildSetProductImageInstruction, buildForceArchiveNFTInstruction, PROGRAM_AUTHORITY_WALLET } from '../utils/popperverseProgram';
import { useAdmin } from '../contexts/AdminContext';
import NFTStatsCard, { readStatsFromReserved } from '../components/NFTStatsCard';
import './Shop.css';

const Shop = () => {
  const navigate = useNavigate();
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { isAdmin } = useAdmin();
  const [cart, setCart] = useState([]);
  
  // Check if connected wallet is the program authority
  const isAuthorityWallet = useMemo(() => {
    if (!publicKey) return false;
    return publicKey.equals(PROGRAM_AUTHORITY_WALLET);
  }, [publicKey]);
  
  // True admin = has admin code AND is program authority wallet
  const canPerformAdminActions = isAdmin && isAuthorityWallet;
  const [solPrice, setSolPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceError, setPriceError] = useState(false);
  const [isProcessing] = useState(false);
  const [winningProducts, setWinningProducts] = useState([]);
  const [loadingWinners, setLoadingWinners] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [nftImageUrl, setNftImageUrl] = useState('');
  const [nftMintAddress, setNftMintAddress] = useState('');
  
  // Stats modal state
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsProduct, setStatsProduct] = useState(null);

  const USD_PRICE = parseFloat(process.env.REACT_APP_7IN_PRICE) || 49.99; // 7-inch plushie price

  // Fetch SOL price from CoinGecko API
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
        const data = await response.json();
        setSolPrice(data.solana.usd);
        setLoading(false);
        setPriceError(false);
      } catch (error) {
        console.error('Error fetching SOL price:', error);
        setSolPrice(200); // Fallback to $200/SOL if API fails
        setLoading(false);
        setPriceError(true);
      }
    };

    fetchSolPrice();
    // Refresh price every 60 seconds
    const interval = setInterval(fetchSolPrice, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch ALL NFTs once, then split into winners + featured
  useEffect(() => {
    const loadAllProducts = async () => {
      if (!connection) return;
      
      setLoadingWinners(true);
      setLoadingFeatured(true);
      
      try {
        console.log('[Shop] Fetching all NFTs from chain (single call)...');
        
        // ONE call to fetch all NFTs
        const allNFTs = await fetchAllPopperNFTs(connection, { votingEligibleOnly: false });
        
        // === WINNERS ===
        const winners = allNFTs.filter(nft => nft.isWinner && !nft.isArchived);
        console.log(`[Shop] Found ${winners.length} winning NFTs`);
        
        if (winners.length > 0) {
          const winnerProducts = await Promise.all(
            winners.map(async (nft, index) => {
              const metadata = await fetchNFTMetadata(connection, nft.nftMint);
              const productCode = `WIN-R${nft.createdInRound || 0}-${(index + 1).toString().padStart(3, '0')}`;
              const stats = nft.reserved ? parseStatsFromReserved(nft.reserved) : null;
              
              return {
                id: nft.nftMint,
                code: productCode,
                productId: nft.productId || 0,
                name: metadata?.name || `Community Winner #${nft.nftMint.slice(0, 6)}`,
                description: metadata?.description || 'Community-voted winning design!',
                image: metadata?.image || '/Design/silhouette.png',
                productImageUri: nft.productImageUri,
                usdPrice: USD_PRICE,
                frame: 'gold',
                creatorWallet: nft.creatorWallet,
                nftHolderWallet: nft.currentOwner,
                totalVotes: nft.totalVotes,
                totalSales: nft.totalSales || 0,
                createdInRound: nft.createdInRound,
                isWinner: true,
                stats: stats?.averages || { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 },
              };
            })
          );
          setWinningProducts(winnerProducts);
        } else {
          setWinningProducts([]);
        }
        setLoadingWinners(false);
        
        // === FEATURED (Round 0) ===
        const round0NFTs = allNFTs.filter(nft => nft.createdInRound === 0 && !nft.isArchived);
        console.log(`[Shop] Found ${round0NFTs.length} round 0 NFTs`);
        
        if (round0NFTs.length > 0) {
          const featuredProds = await Promise.all(
            round0NFTs.map(async (nft, index) => {
              const metadata = await fetchNFTMetadata(connection, nft.nftMint);
              const productCode = `FEAT-R0-${(index + 1).toString().padStart(3, '0')}`;
              const stats = nft.reserved ? parseStatsFromReserved(nft.reserved) : null;
              
              return {
                id: nft.nftMint,
                code: productCode,
                productId: nft.productId || 0,
                name: metadata?.name || `Round 0 Popper #${nft.nftMint.slice(0, 6)}`,
                description: metadata?.description || 'Featured community design!',
                image: metadata?.image || '/Design/silhouette.png',
                productImageUri: nft.productImageUri,
                usdPrice: USD_PRICE,
                frame: index === 0 ? 'orange' : index === 1 ? 'blue' : 'purple',
                creatorWallet: nft.creatorWallet,
                nftHolderWallet: nft.currentOwner,
                totalVotes: nft.totalVotes,
                totalSales: nft.totalSales || 0,
                createdInRound: nft.createdInRound,
                isFeatured: true,
                nftMint: nft.nftMint,
                stats: stats?.averages || { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 },
              };
            })
          );
          setFeaturedProducts(featuredProds);
        } else {
          setFeaturedProducts([]);
        }
        
      } catch (error) {
        console.error('[Shop] Failed to load products:', error);
        setWinningProducts([]);
        setFeaturedProducts([]);
      } finally {
        setLoadingWinners(false);
        setLoadingFeatured(false);
      }
    };
    
    loadAllProducts();
  }, [connection]);

  // Calculate SOL price dynamically based on USD price
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  const calculateSolPrice = () => {
    if (!solPrice) return 0.3; // Fallback
    return (USD_PRICE / solPrice).toFixed(4);
  };
  
  // Helper to parse stats from reserved bytes
  const parseStatsFromReserved = (reserved) => {
    if (!reserved || reserved.length < 121) {
      return { averages: { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 } };
    }
    return readStatsFromReserved(reserved);
  };
  
  // Handle clicking on product card to view stats (for non-admins)
  const handleViewStats = (product, e) => {
    // Only show stats modal for regular users (admins use click for product image editing)
    if (!isAdmin) {
      e.stopPropagation();
      setStatsProduct(product);
      setShowStatsModal(true);
    }
  };

  const handlePurchase = (product) => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    // Navigate to checkout page with order data
    navigate('/checkout', {
      state: {
        product,
        walletAddress: publicKey.toString(),
        basePrice: product.usdPrice,
        solPriceUSD: solPrice
      }
    });
  };

  // Future: Add to cart functionality
  // eslint-disable-next-line no-unused-vars
  const handleAddToCart = (product) => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    setCart([...cart, product]);
    alert(`${product.name} added to cart!`);
  };

  // Handle admin click on product card
  const handleProductCardClick = (product, event) => {
    // Only if admin with authority wallet and not clicking the buy button
    // Allow clicking on winners OR featured products (round 0)
    if (!canPerformAdminActions || event.target.closest('.buy-btn')) return;
    if (!product.isWinner && !product.isFeatured) return;
    
    setSelectedProduct(product);
    setImageUrl(product.productImageUri || '');
    setNftImageUrl(product.image || '');
    setNftMintAddress(product.nftMint || product.id || '');
  };

  // Handle product image upload
  const handleImageUpload = async () => {
    if (!selectedProduct || !publicKey) return;
    if (!canPerformAdminActions) {
      alert('You must be connected with the program authority wallet to perform this action.');
      return;
    }
    if (!imageUrl) {
      alert('Please enter a product image URL');
      return;
    }
    
    setUploading(true);
    
    try {
      // All NFTs (winners and featured): Store product image on-chain
      if (imageUrl) {
        const nftMint = new PublicKey(selectedProduct.id);
        
        console.log('[Shop] Setting product image for NFT:', nftMint.toBase58());
        console.log('[Shop] Product image URL:', imageUrl);
        console.log('[Shop] Admin wallet:', publicKey.toBase58());
        console.log('[Shop] Expected authority:', PROGRAM_AUTHORITY_WALLET.toBase58());
        console.log('[Shop] Wallet matches:', publicKey.equals(PROGRAM_AUTHORITY_WALLET));
        
        if (!publicKey.equals(PROGRAM_AUTHORITY_WALLET)) {
          throw new Error(`Wrong wallet connected. Expected: ${PROGRAM_AUTHORITY_WALLET.toBase58()}, Got: ${publicKey.toBase58()}`);
        }
        
        // Build set_product_image instruction
        const setImageIx = buildSetProductImageInstruction({
          authority: publicKey,
          nftMint,
          productImageUri: imageUrl,
        });
        
        const transaction = new Transaction().add(setImageIx);
        
        // Get initial blockhash for simulation
        let { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = publicKey;
        
        // Simulate first to catch errors before wallet popup
        try {
          const simulation = await connection.simulateTransaction(transaction);
          if (simulation.value.err) {
            console.error('[Shop] Simulation failed:', simulation.value.err);
            console.error('[Shop] Simulation logs:', simulation.value.logs);
            throw new Error(`Transaction would fail: ${JSON.stringify(simulation.value.err)}\n\nLogs: ${simulation.value.logs?.join('\n')}`);
          }
          console.log('[Shop] Simulation passed');
        } catch (simError) {
          if (simError.message.includes('Transaction would fail')) {
            throw simError;
          }
          console.warn('[Shop] Simulation warning:', simError);
        }
        
        // Get FRESH blockhash right before sending to avoid expiry
        const freshBlockhash = await connection.getLatestBlockhash();
        blockhash = freshBlockhash.blockhash;
        lastValidBlockHeight = freshBlockhash.lastValidBlockHeight;
        transaction.recentBlockhash = blockhash;
        
        let signature;
        try {
          signature = await sendTransaction(transaction, connection);
          console.log('[Shop] Transaction sent:', signature);
        } catch (sendError) {
          console.error('[Shop] sendTransaction failed:', sendError);
          console.error('[Shop] Error name:', sendError.name);
          console.error('[Shop] Error message:', sendError.message);
          if (sendError.message?.includes('User rejected')) {
            throw new Error('Transaction was rejected in wallet. Please approve the transaction to continue.');
          }
          throw new Error(`Wallet error: ${sendError.message || sendError}`);
        }
        
        await connection.confirmTransaction({
          signature,
          blockhash,
          lastValidBlockHeight
        }, 'confirmed');
        
        console.log('[Shop] Transaction confirmed');
        
        // Update local state based on product type
        if (selectedProduct.isWinner) {
          setWinningProducts(prev => 
            prev.map(p => 
              p.id === selectedProduct.id 
                ? { ...p, productImageUri: imageUrl }
                : p
            )
          );
        } else if (selectedProduct.isFeatured) {
          setFeaturedProducts(prev => 
            prev.map(p => 
              p.id === selectedProduct.id 
                ? { ...p, productImageUri: imageUrl }
                : p
            )
          );
        }
        
        alert('Product image updated successfully!');
      }
      
      setSelectedProduct(null);
      setImageUrl('');
      setNftImageUrl('');
      setNftMintAddress('');
    } catch (error) {
      console.error('[Shop] Error updating product image:', error);
      alert('Failed to update product image: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle admin force archive (remove NFT from display)
  const handleArchiveNFT = async (product, event) => {
    event.stopPropagation(); // Prevent card click
    
    if (!publicKey || !isAdmin) return;
    
    // Confirm action
    const confirmed = window.confirm(
      `Are you sure you want to remove "${product.name}" from the shop?\n\n` +
      `This will:\n` +
      `- Hide the NFT from all pages\n` +
      `- Mark it as archived on-chain\n` +
      `- User still owns the NFT (can trade on secondary market)\n\n` +
      `This action cannot be undone easily.`
    );
    
    if (!confirmed) return;
    
    try {
      console.log(`[Shop] Archiving NFT: ${product.id}`);
      
      const nftMint = new PublicKey(product.id);
      
      // Build force_archive_nft instruction
      const archiveIx = buildForceArchiveNFTInstruction({
        authority: publicKey,
        nftMint,
      });
      
      const transaction = new Transaction().add(archiveIx);
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;
      
      const signature = await sendTransaction(transaction, connection);
      await connection.confirmTransaction(signature, 'confirmed');
      
      console.log(`[Shop] NFT archived successfully:`, signature);
      
      // Remove from local state
      if (product.isWinner) {
        setWinningProducts(prev => prev.filter(p => p.id !== product.id));
      } else if (product.isFeatured) {
        setFeaturedProducts(prev => prev.filter(p => p.id !== product.id));
      }
      
      alert(`"${product.name}" has been archived and removed from the shop.`);
      
    } catch (error) {
      console.error('[Shop] Error archiving NFT:', error);
      alert(`Failed to archive NFT: ${error.message}`);
    }
  };

  // Render a product card
  const renderProductCard = (product) => {
    const hasProductImage = product.productImageUri;
    const displayImage = hasProductImage ? product.productImageUri : product.image;
    const nftImage = product.image; // Always NFT image on hover
    
    return (
      <div 
        key={product.id} 
        className={`product-card ${product.isWinner ? 'winner-card' : ''} ${product.isFeatured ? 'featured-card' : ''} ${canPerformAdminActions && (product.isWinner || product.isFeatured) ? 'admin-clickable' : ''}`}
        onClick={(e) => handleProductCardClick(product, e)}
        title={canPerformAdminActions && (product.isWinner || product.isFeatured) ? 'Click to add/update product image' : ''}
      >
        {product.isWinner && (
          <div className="winner-badge">🏆 Community Winner</div>
        )}
        {product.isFeatured && (
          <div className="featured-badge">✨ Featured</div>
        )}
        {canPerformAdminActions && (product.isWinner || product.isFeatured) && !hasProductImage && (
          <div className="needs-image-badge">📷 Add Photo</div>
        )}
        {canPerformAdminActions && (product.isWinner || product.isFeatured) && (
          <button 
            className="archive-btn"
            onClick={(e) => handleArchiveNFT(product, e)}
            title="Remove NFT from shop (archive)"
          >
            ✕
          </button>
        )}
        <div className={`product-frame ${product.frame}-bg dual-image-container`}>
          <img 
            src={displayImage} 
            alt={product.name}
            className="product-image shop-image primary-image"
            onError={(e) => { e.target.src = '/Design/silhouette.png'; }}
          />
          {hasProductImage && (
            <img 
              src={nftImage} 
              alt={`${product.name} - Original Design`}
              className="product-image shop-image hover-image"
              onError={(e) => { e.target.src = '/Design/silhouette.png'; }}
            />
          )}
        </div>
        <h4>{product.name}</h4>
        {product.totalVotes > 0 && (
          <p className="vote-count">🗳️ {product.totalVotes} votes</p>
        )}
        <p className="price">{calculateSolPrice()} SOL</p>
        <p className="usd-price">${product.usdPrice} USD</p>
        <div className="product-buttons">
          <button 
            className="stats-btn"
            onClick={(e) => handleViewStats(product, e)}
          >
            📊 Stats
          </button>
          <button 
            className="buy-btn"
            onClick={() => handlePurchase(product)}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Pre-order'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="shop-page">
      <h2>Shop All Plushies</h2>
      
      {/* Admin Panel Banner */}
      {isAdmin && (
        <div className={`admin-banner ${!isAuthorityWallet ? 'admin-warning' : ''}`}>
          <div className="admin-banner-content">
            <span className="admin-icon">🛠️</span>
            <div className="admin-text">
              <strong>ADMIN MODE</strong>
              {isAuthorityWallet ? (
                <p>Click on any featured or winner NFT card to add/update product images</p>
              ) : (
                <p className="warning-text">
                  ⚠️ Connect program authority wallet to perform admin actions<br/>
                  <small>Required: {PROGRAM_AUTHORITY_WALLET.toBase58().slice(0, 8)}...{PROGRAM_AUTHORITY_WALLET.toBase58().slice(-8)}</small>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin Product Management Panel */}
      {canPerformAdminActions && selectedProduct && (
        <div className="admin-panel">
          <div className="admin-panel-header">
            <h3>📦 Add/Update Product Image</h3>
            <button className="close-admin-panel" onClick={() => {
              setSelectedProduct(null);
              setImageUrl('');
              setNftImageUrl('');
              setNftMintAddress('');
            }}>
              ✕ Close
            </button>
          </div>
          
          <p className="admin-subtitle">
            Add a photo of the real plushie product (shows by default, NFT design shows on hover)
          </p>
          
          {/* NFT Info Section */}
          <div className="nft-info-section">
            <div className="nft-info-row">
              <span className="nft-info-label">NFT Name:</span>
              <span className="nft-info-value">{selectedProduct.name}</span>
            </div>
            <div className="nft-info-row">
              <span className="nft-info-label">NFT Mint:</span>
              <span className="nft-info-value nft-mint-address">
                {selectedProduct.id}
                <button 
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedProduct.id);
                    alert('Mint address copied!');
                  }}
                  title="Copy mint address"
                >
                  📋
                </button>
              </span>
            </div>
            {selectedProduct.image && (
              <div className="nft-info-row">
                <span className="nft-info-label">NFT Metadata:</span>
                <a 
                  href={selectedProduct.image} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="nft-info-link"
                >
                  View on Arweave ↗
                </a>
              </div>
            )}
          </div>
          
          <div className="admin-content">
            <div className="image-preview-section">
              <h4>Current Images</h4>
              <div className="image-preview-grid">
                <div className="preview-item">
                  <p className="preview-label">NFT Design (hover)</p>
                  <div className="preview-frame">
                    <img src={selectedProduct.image} alt="NFT Design" />
                  </div>
                </div>
                {selectedProduct.productImageUri && (
                  <div className="preview-item">
                    <p className="preview-label">Product Photo (default)</p>
                    <div className="preview-frame">
                      <img src={selectedProduct.productImageUri} alt="Product" />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="admin-form">
              {/* Product Photo Section */}
              <div className="form-section">
                <label htmlFor="imageUrl">📦 Product Photo URL:</label>
                <input 
                  id="imageUrl"
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://arweave.net/... (physical product photo)"
                  className="admin-input"
                />
                <p className="help-text">
                  Shows by default. Photo of the actual plushie product.
                </p>
              </div>
              
              <div className="admin-actions">
                <button 
                  className="admin-btn cancel-btn"
                  onClick={() => {
                    setSelectedProduct(null);
                    setImageUrl('');
                    setNftImageUrl('');
                    setNftMintAddress('');
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button 
                  className="admin-btn save-btn"
                  onClick={handleImageUpload}
                  disabled={uploading || !imageUrl}
                >
                  {uploading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {priceError && (
        <div className="price-warning">
          <p>⚠️ Using fallback pricing. Live prices may differ.</p>
        </div>
      )}

      {solPrice && (
        <div className="price-info">
          <p>Current SOL Price: ${solPrice.toFixed(2)} USD</p>
        </div>
      )}
      
      {cart.length > 0 && (
        <div className="cart-notice">
          <p>🛒 {cart.length} item(s) in cart</p>
        </div>
      )}

      {/* Community Winners Section */}
      {winningProducts.length > 0 && (
        <section className="winners-section">
          <h3>🏆 Community-Voted Winners</h3>
          <p className="section-subtitle">These designs were voted by the community and are now available for pre-order!</p>
          <div className="product-grid">
            {winningProducts.map(renderProductCard)}
          </div>
        </section>
      )}

      {loadingWinners && (
        <div className="loading-notice">
          <p>Loading community winners...</p>
        </div>
      )}

      {/* Featured Products Section */}
      {featuredProducts.length > 0 && (
        <section className="featured-section">
          <h3>✨ Featured Poppers</h3>
          {loadingFeatured ? (
            <div className="loading-notice">
              <p>Loading featured products...</p>
            </div>
          ) : (
            <div className="product-grid">
              {featuredProducts.map(renderProductCard)}
            </div>
          )}
        </section>
      )}

      {!publicKey && (
        <div className="wallet-notice">
          <p>⚠️ Connect your wallet to purchase plushies!</p>
        </div>
      )}
      
      {/* NFT Stats Modal */}
      {showStatsModal && statsProduct && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="stats-modal-container" onClick={(e) => e.stopPropagation()}>
            <NFTStatsCard
              name={statsProduct.name}
              description={statsProduct.description}
              imageUrl={statsProduct.image}
              stats={statsProduct.stats}
              totalVotes={statsProduct.totalVotes}
              totalSales={statsProduct.totalSales || 0}
              onClose={() => setShowStatsModal(false)}
              showCloseButton={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
